import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'

import { getChartModeTypes, getDefaultChartMode } from '@aytm/app/stats/page/question-views/mn/lib/chart/chartModes'
import { getSectionsAnswer } from '@aytm/app/stats/page/question-views/mn/components/sections-section/chart/lib/answer'
import { getTraitValues } from '@aytm/app/stats/page/question-views/mn/lib/chart/traitValues'
import { getYAxisTitle, getYAxisLimits } from '@aytm/app/stats/page/question-views/mn/lib/chart/yAxis'
import {
  SECTION_PERCENT_CAPABLE_VIEWS,
  SECTION_VIEW_OPTIONS
} from '@aytm/app/stats/page/question-views/mn/lib/viewOptions'

const SectionsChart = ({
  questionKey,
  qType,
  viewMode,
  sections,
  answer,
  sortOrder,
  chartMode: chartModeProp,
  globalChartMode,
  onChartModeChange,
  zoomLevel,
  globalZoomLevel,
  onZoomChange,
  traitName,
  trait,
  priceTemplate,
  // root-format flags, derived coffee-side — the legacy chart model never received
  // the format object itself, so React only sees the pre-computed flags
  ignoreTraits = false,
  percentsOnly = false,
  ignoreAmount = false,
  colorTheme,
  chartLegendMode,
  confidenceLevel,
  significanceLevel,
  confidenceLevels,
  features
}) => {
  const itemsCount = sections?.length || 0

  const chartModeTypes = useMemo(
    () => getChartModeTypes({ viewMode, percentCapableViews: SECTION_PERCENT_CAPABLE_VIEWS, itemsCount }),
    [viewMode, itemsCount]
  )

  const chartMode = useMemo(
    () =>
      getDefaultChartMode({
        chartMode: chartModeProp,
        globalChartMode,
        viewMode,
        percentCapableViews: SECTION_PERCENT_CAPABLE_VIEWS,
        itemsCount
      }),
    [chartModeProp, globalChartMode, viewMode, itemsCount]
  )

  const traitValues = useMemo(() => getTraitValues(trait), [trait])

  const hcAnswers = useMemo(
    () => getSectionsAnswer({ answer, sections, viewMode, priceTemplate, trait, traitName }),
    [answer, sections, viewMode, priceTemplate, trait, traitName]
  )

  const yAxisTitle = useMemo(
    () =>
      getYAxisTitle({
        viewMode,
        chartMode,
        options: SECTION_VIEW_OPTIONS,
        percentCapableViews: SECTION_PERCENT_CAPABLE_VIEWS
      }),
    [viewMode, chartMode]
  )
  // Split by trait plots one bar per trait value; limits taken from the ungrouped amounts would
  // clip the series actually drawn, so the axis is left to fit itself.
  const yAxisLimits = useMemo(
    () =>
      trait
        ? false
        : getYAxisLimits({ viewMode, items: sections, hcAnswers, skipViewModes: SECTION_PERCENT_CAPABLE_VIEWS }),
    [viewMode, sections, hcAnswers, trait]
  )

  // selector highlights the explicit per-question choice; unset/implicit → Default
  // (mirrors legacy renderChartModes — NOT the resolved mode ec/mx show)
  const selectorChartModeValue =
    chartModeProp === 'off' || chartModeTypes.includes(chartModeProp) ? chartModeProp : 'off'

  const ignorePercents = percentsOnly || ignoreAmount || !SECTION_PERCENT_CAPABLE_VIEWS.includes(viewMode)

  return (
    <ChartContainer
      questionKey={questionKey}
      qType={qType}
      aType="SQ"
      chartMode={chartMode}
      selectorChartModeValue={selectorChartModeValue}
      chartModeTypes={chartModeTypes}
      viewOptionsDisabled={features?.view_options?.disabled}
      onChartModeChange={onChartModeChange}
      answerVariants={sections}
      answer={hcAnswers}
      sortOrder={sortOrder}
      yAxisTitle={yAxisTitle}
      yAxisLimits={yAxisLimits}
      zoomLevel={zoomLevel}
      globalZoomLevel={globalZoomLevel}
      zoomType
      onZoomChange={onZoomChange}
      traitName={traitName}
      trait={trait}
      traitValues={traitValues}
      ignoreTraits={ignoreTraits}
      ignorePercents={ignorePercents}
      viewMode={viewMode}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      confidenceLevel={confidenceLevel}
      significanceLevel={significanceLevel}
      confidenceLevels={confidenceLevels}
      features={features}
    />
  )
}

SectionsChart.propTypes = {
  questionKey: PropTypes.string,
  qType: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(SECTION_VIEW_OPTIONS.map(o => o.value)).isRequired,
  sections: PropTypes.array,
  answer: answerPropType,
  sortOrder: PropTypes.string,
  chartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  onChartModeChange: PropTypes.func.isRequired,
  zoomLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  globalZoomLevel: PropTypes.string,
  onZoomChange: PropTypes.func,
  traitName: PropTypes.string,
  trait: PropTypes.object,
  priceTemplate: PropTypes.string,
  ignoreTraits: PropTypes.bool,
  percentsOnly: PropTypes.bool,
  ignoreAmount: PropTypes.bool,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  features: PropTypes.object
}

SectionsChart.displayName = 'SectionsChart'

export default SectionsChart
